export const DISPATCH_REASSIGNMENT_MODE_COMPLETE = "complete";
export const DISPATCH_REASSIGNMENT_MODE_FAST = "fast";
export const DEFAULT_DISPATCH_PROTECT_AFTER_REASSIGNMENT = true;

const DISPATCH_REASSIGNMENT_MODE_STORAGE_PREFIX = "grain:dispatch-reassignment-mode:";
const DISABLED_FEATURE_FLAG_VALUES = new Set(["0", "false", "no", "off"]);
const ENABLED_BOOLEAN_VALUES = new Set(["1", "true", "yes", "on"]);

export function normalizeDispatchReassignmentMode(
  value,
  fallback = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (
    normalizedValue === DISPATCH_REASSIGNMENT_MODE_COMPLETE
    || normalizedValue === DISPATCH_REASSIGNMENT_MODE_FAST
  ) {
    return normalizedValue;
  }
  return fallback;
}

export function isDispatchFastReassignmentMode(value) {
  return normalizeDispatchReassignmentMode(value) === DISPATCH_REASSIGNMENT_MODE_FAST;
}

export function isUntargetedDispatchReactivationAllowed(value) {
  return !isDispatchFastReassignmentMode(value);
}

export function resolveDispatchFastReassignmentEnabled(value) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return !DISABLED_FEATURE_FLAG_VALUES.has(normalizedValue);
}

export function buildDispatchReassignmentModeStorageKey(userId) {
  const normalizedUserId = typeof userId === "string" ? userId.trim() : "";
  return normalizedUserId
    ? `${DISPATCH_REASSIGNMENT_MODE_STORAGE_PREFIX}${encodeURIComponent(normalizedUserId)}`
    : "";
}

export function readStoredDispatchReassignmentMode(userId, { targetWindow } = {}) {
  const storageKey = buildDispatchReassignmentModeStorageKey(userId);
  const resolvedWindow = targetWindow || (typeof window !== "undefined" ? window : null);
  if (!storageKey || !resolvedWindow?.localStorage) {
    return DISPATCH_REASSIGNMENT_MODE_COMPLETE;
  }

  try {
    return normalizeDispatchReassignmentMode(resolvedWindow.localStorage.getItem(storageKey));
  } catch {
    return DISPATCH_REASSIGNMENT_MODE_COMPLETE;
  }
}

export function persistStoredDispatchReassignmentMode(userId, mode, { targetWindow } = {}) {
  const storageKey = buildDispatchReassignmentModeStorageKey(userId);
  const normalizedMode = normalizeDispatchReassignmentMode(mode);
  const resolvedWindow = targetWindow || (typeof window !== "undefined" ? window : null);
  if (!storageKey || !resolvedWindow?.localStorage) {
    return normalizedMode;
  }

  try {
    resolvedWindow.localStorage.setItem(storageKey, normalizedMode);
  } catch {
    // La préférence reste active en mémoire lorsque le stockage navigateur est indisponible.
  }
  return normalizedMode;
}

export function normalizeDispatchProtectAfterReassignment(
  value,
  fallback = DEFAULT_DISPATCH_PROTECT_AFTER_REASSIGNMENT,
) {
  if (typeof value === "boolean") {
    return value;
  }

  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (DISABLED_FEATURE_FLAG_VALUES.has(normalizedValue)) {
    return false;
  }
  if (ENABLED_BOOLEAN_VALUES.has(normalizedValue)) {
    return true;
  }
  return fallback;
}

export function appendDispatchReassignmentModeSearchParam(searchParams, mode) {
  if (searchParams instanceof URLSearchParams) {
    searchParams.set("rebuild_mode", normalizeDispatchReassignmentMode(mode));
  }
  return searchParams;
}

export function buildDispatchReassignmentMutationModeFields(mode, { allowOvertime = false } = {}) {
  const normalizedMode = normalizeDispatchReassignmentMode(mode);
  return {
    rebuild_mode: normalizedMode,
    ...(allowOvertime ? { allow_overtime: true } : {}),
  };
}

export function buildDispatchProtectAfterReassignmentMutationFields(protectAfterReassignment) {
  return normalizeDispatchProtectAfterReassignment(protectAfterReassignment)
    ? {}
    : { protect_after_reassignment: false };
}
